/**
 * Helpers for "fresh chat on return" + not stacking blank drafts in Recent.
 * Kept pure so the rules can be unit-tested without mounting ChatAgentPanel.
 */

export function hasUserMessage(messages) {
  return Array.isArray(messages) && messages.some((m) => m?.sender === 'user');
}

export function isDefaultNewChatTitle(title) {
  const t = (title || '').trim();
  return !t || t === 'New chat';
}

/**
 * Session types that are never treated as disposable blank drafts —
 * 'onboarding' (pre-existing) and 'planning' (My Path, added W2.6): a
 * planning session is titled "New Path" and has no user chat message yet
 * immediately after creation (see path_session_service.create_planning_session),
 * which is exactly the shape every predicate below uses to detect a blank
 * draft. Without this carve-out a fresh planning session would be reused,
 * discarded on navigation, hidden from the sidebar, or background-deleted
 * as an orphan the same way an empty "New chat" draft is.
 */
const PROTECTED_SESSION_TYPES = new Set(['onboarding', 'planning']);

/**
 * Reuse the open empty top-level draft instead of creating another session.
 */
export function shouldReuseEmptyDraft({ projectId, sessionId, messages, sessionType }) {
  return Boolean(
    !projectId
    && sessionId
    && !hasUserMessage(messages)
    && !PROTECTED_SESSION_TYPES.has(sessionType)
  );
}

/**
 * When switching away from the open chat, delete it if it still has no user
 * content. Safe because the open session's messages are already loaded.
 */
export function shouldDiscardEmptyDraftOnSelect({
  prevSessionId,
  nextSessionId,
  messages,
  sessionType,
}) {
  return Boolean(
    prevSessionId
    && nextSessionId
    && prevSessionId !== nextSessionId
    && !hasUserMessage(messages)
    && !PROTECTED_SESSION_TYPES.has(sessionType)
  );
}

/**
 * Hide blank drafts from Recent: the active empty composer surface, and any
 * non-active row still titled "New chat" (orphans left after switching away).
 */
export function shouldHideSessionFromSidebar(session, { activeSessionId, activeHasUserMessages }) {
  if (!session || PROTECTED_SESSION_TYPES.has(session.session_type)) return false;

  const isActive = session.id === activeSessionId;
  if (isActive && !activeHasUserMessages) return true;

  if (!isActive && isDefaultNewChatTitle(session.title)) return true;

  return false;
}

/**
 * Sessions that should be deleted in the background so orphans do not linger.
 * `protectIds` keeps the open draft / just-created draft safe during races.
 */
export function orphanEmptyDraftIds(sessions, activeSessionId, protectIds = []) {
  if (!Array.isArray(sessions)) return [];
  const protect = new Set([activeSessionId, ...protectIds].filter(Boolean));
  return sessions
    .filter((s) => (
      s
      && s.id
      && !protect.has(s.id)
      && !PROTECTED_SESSION_TYPES.has(s.session_type)
      && isDefaultNewChatTitle(s.title)
    ))
    .map((s) => s.id);
}
